import * as effects from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import * as actions from './actions'
import * as api from './api'
import history from '../../utils/history'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import {
  EditCourseType,
  CreateThemeType,
  EditThemeType,
  RemoveThemeType,
} from './types'

function* fetchCourse(payload: { id: string }): SagaIterator {
  try {
    const course = yield effects.call(api.fetchCourse, payload.id)
    yield effects.put(actions.fetchCourseSuccess(course.data))
  } catch (error) {
    console.warn('Cannot fetch course', error)
    yield effects.put(actions.fetchCourseFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchThemes(payload: { id: string }): SagaIterator {
  try {
    const themes = yield effects.call(api.fetchThemes, payload.id)
    yield effects.put(actions.fetchThemesSuccess(themes.data))
  } catch (error) {
    console.warn('Cannot fetch themes', error)
    yield effects.put(actions.fetchThemesFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editCourse(payload: EditCourseType): SagaIterator {
  try {
    const { id, title, status, starts_at, auto_subscription } = payload
    yield effects.call(
      api.editCourse,
      id,
      title,
      status,
      starts_at,
      auto_subscription
    )
    yield effects.put(actions.editCourseSuccess())
    yield effects.call([history, history.push], '/admin/courses')
  } catch (error) {
    console.warn('Cannot edit course', error)
    yield effects.put(actions.editCourseFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeCourse(payload: { id: string }): SagaIterator {
  try {
    yield effects.call(api.removeCourse, payload.id)
    yield effects.put(actions.removeCourseSuccess())
    yield effects.call([history, history.push], '/admin/courses')
  } catch (error) {
    console.warn('Cannot remove course', error)
    yield effects.put(actions.removeCourseFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchCourseStatistic(payload: { id: string }): SagaIterator {
  try {
    const statistic = yield effects.call(api.fetchCourseStatistic, payload.id)
    yield effects.put(actions.fetchCourseStatisticSuccess(statistic))
  } catch (error) {
    console.warn('Cannot fetch course statistic', error)
    yield effects.put(
      actions.fetchCourseStatisticFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* createTheme(payload: CreateThemeType): SagaIterator {
  try {
    const { id, title, color } = payload
    yield effects.call(api.createTheme, id, title, color)
    yield effects.put(actions.fetchThemes(id))
    yield effects.put(actions.createThemeSuccess())
  } catch (error) {
    console.warn('Cannot create theme', error)
    yield effects.put(actions.createThemeFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editTheme(payload: EditThemeType): SagaIterator {
  try {
    const { id, themeId, title, color } = payload
    yield effects.call(api.editTheme, themeId, title, color)
    yield effects.put(actions.fetchThemes(id))
    yield effects.put(actions.editThemeSuccess())
  } catch (error) {
    console.warn('Cannot edit theme', error)
    yield effects.put(actions.editThemeFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeTheme(payload: RemoveThemeType): SagaIterator {
  try {
    const { id, themeId } = payload
    yield effects.call(api.removeTheme, themeId)
    yield effects.put(actions.fetchThemes(id))
    yield effects.put(actions.removeThemeSuccess())
  } catch (error) {
    console.warn('Cannot remove theme', error)
    yield effects.put(actions.removeThemeFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createMap(payload: {
  course_id: string
  image: File
  is_active: boolean
}): SagaIterator {
  try {
    const { course_id, image, is_active } = payload
    yield effects.call(api.createMap, course_id, image, is_active)
    yield effects.put(actions.createMapSuccess())
    yield effects.put(actions.fetchCourse(course_id))
  } catch (error) {
    console.warn('Cannot create map', error)
    yield effects.put(actions.createMapFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeMap(payload: {
  course_id: string
  map_id: string
}): SagaIterator {
  try {
    const { course_id, map_id } = payload
    yield effects.call(api.removeMap, map_id)
    yield effects.put(actions.removeMapSuccess())
    yield effects.put(actions.fetchCourse(course_id))
  } catch (error) {
    console.warn('Cannot remove map', error)
    yield effects.put(actions.removeMapFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchCourse(): SagaIterator {
  yield effects.takeEvery(actions.fetchCourse.toString(), fetchCourse)
}

function* watchFetchThemes(): SagaIterator {
  yield effects.takeEvery(actions.fetchThemes.toString(), fetchThemes)
}

function* watchEditCourse(): SagaIterator {
  yield effects.takeEvery(actions.editCourse.toString(), editCourse)
}

function* watchRemoveCourse(): SagaIterator {
  yield effects.takeEvery(actions.removeCourse.toString(), removeCourse)
}

function* watchFetchCourseStatistic(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchCourseStatistic.toString(),
    fetchCourseStatistic
  )
}

function* watchCreateTheme(): SagaIterator {
  yield effects.takeEvery(actions.createTheme.toString(), createTheme)
}

function* watchEditTheme(): SagaIterator {
  yield effects.takeEvery(actions.editTheme.toString(), editTheme)
}

function* watchRemoveTheme(): SagaIterator {
  yield effects.takeEvery(actions.removeTheme.toString(), removeTheme)
}

function* watchCreateMap(): SagaIterator {
  yield effects.takeEvery(actions.createMap.toString(), createMap)
}

function* watchRemoveMap(): SagaIterator {
  yield effects.takeEvery(actions.removeMap.toString(), removeMap)
}

export default function* watchAdminEditCourse(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchCourse),
    effects.fork(watchEditCourse),
    effects.fork(watchRemoveCourse),
    effects.fork(watchFetchCourseStatistic),
    effects.fork(watchFetchThemes),
    effects.fork(watchCreateTheme),
    effects.fork(watchEditTheme),
    effects.fork(watchRemoveTheme),
    effects.fork(watchCreateMap),
    effects.fork(watchRemoveMap),
  ])
}
